
import { Question } from '../types';

export const qur101AhrufQuestions: Question[] = [
  {
    id: 'qur091',
    text: 'What does "the seven aḥruf" of the Qur\'ān refer to?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Small dialectical changes in certain parts of the Qur\'ān', isCorrect: true },
      { id: 'b', text: 'Not literally seven letters but modes of recitation', isCorrect: true },
      { id: 'c', text: 'Divine canonical recitations from Allāh', isCorrect: true },
      { id: 'd', text: 'Seven completely different versions of the Qur\'ān', isCorrect: false }
    ],
    explanation: 'The "seven aḥruf" refers to small dialectical changes or modes of recitation in certain parts of the Qur\'ān, not literally seven letters. These are divine canonical recitations that came from Allāh SWT, occurring in specific parts of the Qur\'ān to accommodate different Arab dialects and make recitation easier.',
    topic: 'qur101'
  },
  {
    id: 'qur092',
    text: 'How is the authenticity of the concept of seven aḥruf established?',
    type: 'single',
    options: [
      { id: 'a', text: 'Through mutawātir narrations from many companions', isCorrect: true },
      { id: 'b', text: 'Through archaeological evidence of early manuscripts', isCorrect: false },
      { id: 'c', text: 'Through linguistic analysis by later scholars', isCorrect: false },
      { id: 'd', text: 'Through consensus reached during the Abbasid period', isCorrect: false }
    ],
    explanation: 'The authenticity of the seven aḥruf is established through mutawātir narrations—numerous reports of the highest level of authenticity from different companions across different time frames. These consistent reports make the concept of seven aḥruf definitively established (qat\'ī) in Islamic tradition.',
    topic: 'qur101'
  },
  {
    id: 'qur093',
    text: 'What does the narration about the Qur\'ān being "easy to read, yet with deeper meanings" tell us about aḥruf?',
    type: 'single',
    options: [
      { id: 'a', text: 'The different aḥruf help make recitation accessible while preserving depth', isCorrect: true },
      { id: 'b', text: 'Each ḥarf has a completely different meaning', isCorrect: false },
      { id: 'c', text: 'Only scholars should recite in different aḥruf', isCorrect: false },
      { id: 'd', text: 'The aḥruf were a temporary concession before standardization', isCorrect: false }
    ],
    explanation: 'The narration about the Qur\'ān being "easy to read, yet with deeper meanings" indicates that the different aḥruf serve to make recitation accessible to people of different dialects and linguistic backgrounds, while still preserving the depth and richness of meaning that can be endlessly expanded upon in study and reflection.',
    topic: 'qur101'
  },
  {
    id: 'qur094',
    text: 'According to narrations, how did the Prophet ﷺ receive the seven aḥruf?',
    type: 'single',
    options: [
      { id: 'a', text: 'He received one ḥarf initially, then requested more from Allāh through Jibrīl', isCorrect: true },
      { id: 'b', text: 'He received all seven simultaneously during the Night of Power', isCorrect: false },
      { id: 'c', text: 'He developed them himself to accommodate different tribes', isCorrect: false },
      { id: 'd', text: 'They were revealed only after the migration to Madinah', isCorrect: false }
    ],
    explanation: 'According to narrations, such as the one from Ubai ibn Ka\'b, the Prophet ﷺ initially received the Qur\'ān in one ḥarf. He then requested from Allāh, through Jibrīl, that accommodations be made for his ummah who wouldn\'t be able to handle only a single method. Jibrīl brought additional aḥruf one by one until Allāh permitted all seven, each being valid.',
    topic: 'qur101'
  },
  {
    id: 'qur095',
    text: 'What incident illustrates companions\' initial unfamiliarity with the existence of different aḥruf?',
    type: 'single',
    options: [
      { id: 'a', text: 'A companion heard another reciting Sūrat ul-Furqān differently and brought him to the Prophet ﷺ', isCorrect: true },
      { id: 'b', text: 'Companions argued about the first revelation at a gathering', isCorrect: false },
      { id: 'c', text: 'Different tribes couldn\'t understand each other\'s recitation at prayer', isCorrect: false },
      { id: 'd', text: 'A companion accused the scribes of making errors in writing', isCorrect: false }
    ],
    explanation: 'An incident that illustrates companions\' initial unfamiliarity with different aḥruf involves a companion who heard another reciting Sūrat ul-Furqān differently than he had learned it. He brought this person to the Prophet ﷺ, who had each recite. The Prophet ﷺ confirmed both were correct, explaining that the Qur\'ān was revealed in seven aḥruf, all of which were valid.',
    topic: 'qur101'
  },
  {
    id: 'qur096',
    text: 'What wisdom lies behind Allāh revealing the Qur\'ān in seven aḥruf?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'To make recitation easier for people of different dialects', isCorrect: true },
      { id: 'b', text: 'To demonstrate that the message transcends specific words', isCorrect: true },
      { id: 'c', text: 'As a mercy to the Prophet\'s ﷺ ummah', isCorrect: true },
      { id: 'd', text: 'To create a hierarchy among Muslims based on which ḥarf they used', isCorrect: false }
    ],
    explanation: 'The wisdom behind the seven aḥruf includes: making recitation easier for people of different dialects; demonstrating that the message transcends specific words while maintaining its essence; and serving as a mercy to the Prophet\'s ﷺ ummah by accommodating diverse linguistic backgrounds. This facilitated Islam\'s mission to bring people together from different backgrounds.',
    topic: 'qur101'
  },
  {
    id: 'qur097',
    text: 'What are the different scholarly definitions of the seven aḥruf?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Different expressions or phrases throughout the Qur\'ān', isCorrect: true },
      { id: 'b', text: 'Seven Arabic dialects', isCorrect: true },
      { id: 'c', text: 'Seven types of changes or modes', isCorrect: true },
      { id: 'd', text: 'Seven levels of spiritual interpretation', isCorrect: false }
    ],
    explanation: 'Scholars have proposed different definitions of the seven aḥruf: different expressions or phrases where one can choose the most eloquent; seven Arabic dialects (Quraishi, Thaqīf, Ḥawāzin, Kinān, Tamīm, Yemeni, etc.); and seven types of changes or modes (affecting vowelization, verb tense, singular/plural, etc.). The "seven categories" definition is generally refuted.',
    topic: 'qur101'
  },
  {
    id: 'qur098',
    text: 'What types of changes might occur within the seven aḥruf?',
    type: 'multiple',
    options: [
      { id: 'a', text: 'Changes in vowelization', isCorrect: true },
      { id: 'b', text: 'Changes in word order', isCorrect: true },
      { id: 'c', text: 'Use of synonyms with similar meanings', isCorrect: true },
      { id: 'd', text: 'Changes that alter fundamental beliefs', isCorrect: false }
    ],
    explanation: 'Types of changes that might occur within the seven aḥruf include: changes in vowelization; changes in word order; and the use of synonyms with similar meanings. Importantly, these differences are minute and only in certain words, never altering fundamental beliefs or rulings. Some scholars also include changes in verb tense, feminine/masculine forms, or singular/plural forms.',
    topic: 'qur101'
  },
  {
    id: 'qur099',
    text: 'Which of the following statements about admission and omission of words in aḥruf is most accurate?',
    type: 'single',
    options: [
      { id: 'a', text: 'Most scholars refute this as a type of difference in aḥruf', isCorrect: true },
      { id: 'b', text: 'This is the most common type of difference', isCorrect: false },
      { id: 'c', text: 'This is only permitted for non-essential words', isCorrect: false },
      { id: 'd', text: 'This was endorsed by all major schools of thought', isCorrect: false }
    ],
    explanation: 'Regarding admission and omission of words as a type of difference in aḥruf, most scholars refute this category. While some early scholars included it in theoretical discussions of possible differences, it is generally not accepted as a legitimate type of variation within the canonical recitations we have today.',
    topic: 'qur101'
  },
  {
    id: 'qur100',
    text: 'What is the relationship between aḥruf and qirā\'āt?',
    type: 'single',
    options: [
      { id: 'a', text: 'Aḥruf are the ingredients, qirā\'āt are the science of combining them into canonical recitations', isCorrect: true },
      { id: 'b', text: 'They are exactly the same thing with different names', isCorrect: false },
      { id: 'c', text: 'Aḥruf were replaced by qirā\'āt during Uthman\'s standardization', isCorrect: false },
      { id: 'd', text: 'Aḥruf are the spiritual meanings, qirā\'āt are the verbal expressions', isCorrect: false }
    ],
    explanation: 'The relationship between aḥruf and qirā\'āt is that aḥruf are the ingredients—the divine variants permitted by Allāh—while qirā\'āt are the science of combining these aḥruf into canonical recitations. Qirā\'āt are systematized methods of recitation that have authenticated chains of transmission (isnād) back to the Prophet ﷺ.',
    topic: 'qur101'
  }
];
